import dayjs from "@calcom/dayjs";

export interface DateBlockConfig {
  /** date-block range start (inclusive). Null if date-block param absent. */
  blockFrom: Date | null;
  /** date-block range end (inclusive). Null if date-block param absent. */
  blockTo: Date | null;
  /** Number of days in the block range. 0 if date-block absent. */
  days: number;
  /** date-fill: also block from today to blockFrom (bridge the gap). */
  fill: boolean;
  /** date-min: block all dates strictly before this date. */
  minDate: Date | null;
  /** date-max: block all dates strictly after this date. */
  maxDate: Date | null;
}

const MAX_DAYS = 365;

/**
 * Parse URL date-blocking params. Returns null only if none of the params are present.
 *
 * Supported params (all formats YYYY-MM-DD, same as &date= in Cal.com):
 *  - date-block=N          : block a range of N days (1–365)
 *  - date-from-block=DATE  : start of the block range (default: today)
 *  - date-fill             : also block from today to date-from-block (bridge the gap)
 *  - date-min=DATE         : block all dates before DATE (absolute cutoff)
 *  - date-max=DATE         : block all dates after DATE (absolute cutoff)
 *
 * Examples:
 *  ?date-block=14&date-from-block=2026-05-01           → block May 1–14
 *  ?date-block=14&date-from-block=2026-05-01&date-fill → block today → May 14
 *  ?date-min=2026-05-01                                → nothing before May 1
 *  ?date-max=2026-06-01                                → nothing after June 1
 */
export function parseDateBlockParams(
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>
): DateBlockConfig | null {
  const getRaw = (key: string): string | undefined => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.get(key) ?? undefined;
    }
    const val = (searchParams as Record<string, string | string[] | undefined>)[key];
    return Array.isArray(val) ? val[0] : val;
  };

  const hasParam = (key: string): boolean => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.has(key);
    }
    return key in (searchParams as Record<string, unknown>);
  };

  const parseDate = (raw: string | undefined, paramName: string): dayjs.Dayjs | null => {
    if (!raw) return null;
    const parsed = dayjs(raw, "YYYY-MM-DD", true); // strict ISO parse
    if (!parsed.isValid()) {
      console.log(`[DateBlock] Invalid ${paramName} format: "${raw}" — expected YYYY-MM-DD, ignoring`);
      return null;
    }
    return parsed.startOf("day");
  };

  // ── Parse each param ────────────────────────────────────────────────────────

  // date-block + date-from-block
  let blockFrom: dayjs.Dayjs | null = null;
  let blockTo: dayjs.Dayjs | null = null;
  let days = 0;

  const rawDays = getRaw("date-block");
  if (rawDays) {
    days = parseInt(rawDays, 10);
    if (isNaN(days) || days <= 0 || days > MAX_DAYS) {
      console.log("[DateBlock] Invalid date-block value:", rawDays, "— must be 1–365, ignoring");
      days = 0;
    } else {
      const rawFrom = getRaw("date-from-block");
      const parsedFrom = parseDate(rawFrom, "date-from-block");
      blockFrom = parsedFrom ?? dayjs().startOf("day");
      blockTo = blockFrom.add(days - 1, "day").endOf("day").startOf("day");
    }
  }

  // date-fill (presence only — no value needed)
  const fill = hasParam("date-fill");

  // date-min / date-max
  const minDate = parseDate(getRaw("date-min"), "date-min");
  const maxDate = parseDate(getRaw("date-max"), "date-max");

  // Return null only if nothing is active
  if (!blockFrom && !fill && !minDate && !maxDate) return null;

  const config: DateBlockConfig = {
    blockFrom: blockFrom?.toDate() ?? null,
    blockTo: blockTo?.toDate() ?? null,
    days,
    fill,
    minDate: minDate?.toDate() ?? null,
    maxDate: maxDate?.toDate() ?? null,
  };

  console.log("[DateBlock] Config:", {
    ...(blockFrom && { block: `${blockFrom.format("YYYY-MM-DD")} → ${blockTo!.format("YYYY-MM-DD")} (${days}j)` }),
    ...(fill && { fill: `today → ${blockFrom?.format("YYYY-MM-DD") ?? "today"}` }),
    ...(minDate && { minDate: minDate.format("YYYY-MM-DD") }),
    ...(maxDate && { maxDate: maxDate.format("YYYY-MM-DD") }),
  });

  return config;
}

/**
 * Returns true if the given date should be blocked (hidden/disabled in the booking UI).
 * Checks all active rules: date-block range, date-fill, date-min, date-max.
 * Uses core dayjs only — no plugins needed.
 *
 * @param date - YYYY-MM-DD string, Date, or Dayjs
 * @param config - result of parseDateBlockParams, or null (no blocking)
 */
export function isDateBlocked(
  date: string | Date | dayjs.Dayjs,
  config: DateBlockConfig | null
): boolean {
  if (!config) return false;

  const d = dayjs(date).startOf("day");
  const today = dayjs().startOf("day");

  // date-min: block everything strictly before minDate
  if (config.minDate) {
    const min = dayjs(config.minDate).startOf("day");
    if (d.isBefore(min)) return true;
  }

  // date-max: block everything strictly after maxDate
  if (config.maxDate) {
    const max = dayjs(config.maxDate).startOf("day");
    if (d.isAfter(max)) return true;
  }

  // date-block range (+ optional date-fill extension)
  if (config.blockFrom) {
    const blockFrom = dayjs(config.blockFrom).startOf("day");
    const blockTo = dayjs(config.blockTo!).startOf("day");

    if (config.fill) {
      // date-fill: extend block backwards to today
      // Blocked range = [today, blockTo]
      if (!d.isBefore(today) && !d.isAfter(blockTo)) return true;
    } else {
      // Normal range: [blockFrom, blockTo]
      if (!d.isBefore(blockFrom) && !d.isAfter(blockTo)) return true;
    }
  } else if (config.fill) {
    // date-fill without date-block: block today only (edge case, no-op in practice)
    if (d.isSame(today)) return true;
  }

  return false;
}
